import { type ComponentType, lazy } from 'react';

const RELOAD_STATE_KEY = 'lobu:chunk-reload-state';
// Allow a small burst of recovery reloads, then stop. One reload fixes a
// genuinely stale chunk (the reload revalidates the short-lived index.html and
// gets fresh chunk hashes); the cap is what prevents an infinite loop when the
// chunk fails every time regardless (the bug this guards: the narrow side-panel
// layout force-mounts a lazy chunk whose persistent failure reloaded the iframe
// ~hundreds of times/sec).
const MAX_RELOADS_PER_WINDOW = 2;
const RELOAD_WINDOW_MS = 30_000;

export function isChunkLoadError(err: unknown): boolean {
  if (err instanceof Error && err.name === 'ChunkLoadError') return true;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : '';
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Unable to preload CSS')
  );
}

// Reload to recover from a stale/failed dynamic-import chunk, bounded to
// MAX_RELOADS_PER_WINDOW within RELOAD_WINDOW_MS. The count lives in
// sessionStorage so it SURVIVES the reload it triggers — the previous version
// cleared its guard flag at module top-level, which re-runs on that very
// reload, so a chunk that failed every time looped forever. The time window
// re-arms recovery for a later failure (e.g. a new deploy removed the chunk an
// old still-open document references) without ever allowing a tight loop: once
// the cap is hit, further failures surface the error instead of reloading.
// We require sessionStorage to write the guard; if storage is unavailable we
// fail closed (no reload) rather than risk a reload loop on a broken host.
export function reloadForStaleChunk(): boolean {
  try {
    const now = Date.now();
    let count = 0;
    let windowStart = 0;
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { count?: unknown; at?: unknown };
        // Clamp to finite numbers — a non-numeric stored value must not slip
        // past the `count >= MAX` cap via string coercion.
        count = Number.isFinite(parsed.count) ? (parsed.count as number) : 0;
        windowStart = Number.isFinite(parsed.at) ? (parsed.at as number) : 0;
      } catch {
        // Corrupt value — treat as a fresh window.
      }
    }
    // Outside the window (first failure, or a much later one — e.g. a new
    // deploy) → start a fresh burst.
    if (now - windowStart > RELOAD_WINDOW_MS) {
      count = 0;
      windowStart = now;
    }
    // Cap reached: reloading didn't fix it, so stop and let the caller surface
    // the error rather than loop.
    if (count >= MAX_RELOADS_PER_WINDOW) return false;
    sessionStorage.setItem(
      RELOAD_STATE_KEY,
      JSON.stringify({ count: count + 1, at: windowStart })
    );
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

// biome-ignore lint/suspicious/noExplicitAny: matches React.lazy's own bound
export function lazyWithReload<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await loader();
    } catch (err) {
      if (!isChunkLoadError(err)) throw err;
      if (!reloadForStaleChunk()) throw err;
      // Reload was triggered — keep Suspense in its fallback while we go.
      return await new Promise<{ default: T }>(() => {});
    }
  });
}
